import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import searchIcon from "../../assets/noun-search-icon-3670603.svg";
import { useAuth } from "../../contexts/AuthContext";
import { MedewerkerType as Role } from "../../enums";
import { useDossierZoeklijst } from "../../hooks/useDossierZoeklijst";
import { matchtZoekterm } from "../../utils/dossierZoek";
import DossierStatusBadge from "./DossierStatusBadge";
import { isEditableTarget } from "./GlobalShortcuts";
import tableStyles from "../DataTable/DataTable.module.css";

const MAX_RESULTATEN = 20;

export default function DossierQuickSearch() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRowRef = useRef<HTMLTableRowElement>(null);

  // C4: iedereen die een dossier mag openen; de server beperkt arts en
  // assistenten tot hun eigen dossiers (GET /dossiers/zoeklijst).
  const magZoeken = hasRole(
    Role.PM,
    Role.MANAGER,
    Role.FINANCIELE_MANAGER,
    Role.ADMIN,
    Role.INPLANNER,
    Role.ARTS,
    Role.ASSISTENT,
    Role.RADIOLOGIE_ASSISTENT,
    Role.WAARNEMEND_ASSISTENT,
    Role.VOORBEREIDINGS_ASSISTENT,
  );

  const { data: dossiers = [], isLoading } = useDossierZoeklijst({
    enabled: magZoeken && open,
  });

  // Sluiten bewaart de zoektekst (feedback kantoor 1-11-2026): wie naast het
  // venster klikt, vindt zijn zoekopdracht bij het heropenen terug.
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      // Oude tekst geselecteerd, zodat typen hem direct vervangt.
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, close]);

  // Ctrl+Z / Cmd+Z opent (of sluit) de snelzoeker; Escape sluit. In tekstvelden
  // blijft Ctrl+Z gewoon "ongedaan maken".
  useEffect(() => {
    if (!magZoeken) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        close();
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if (e.key.toLowerCase() !== "z") return;
      if (isEditableTarget(e)) return;
      e.preventDefault();
      if (open) {
        close();
      } else {
        setOpen(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [magZoeken, open, close]);

  const zoekterm = query.trim().toLowerCase();
  const resultaten = useMemo(() => {
    if (!zoekterm) return [];
    // Elk los woord moet ergens in naam of kenmerk voorkomen (D8), zodat
    // "berg 010126" of "HLRV ber" het dossier vindt.
    return dossiers
      .filter((d) => matchtZoekterm(d.naam, d.kenmerk, zoekterm))
      .slice(0, MAX_RESULTATEN);
  }, [dossiers, zoekterm]);

  useEffect(() => {
    setActiveIndex(0);
  }, [zoekterm]);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!magZoeken) return null;

  const openDossier = (kenmerk: string) => {
    close();
    navigate(`/dossier/${kenmerk}`);
  };

  const celStijl: React.CSSProperties = { padding: "4px 8px", fontSize: 12 };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "fixed", left: 10, bottom: 62, zIndex: 1000 }}
    >
      <button
        aria-label="Dossier zoeken (Ctrl+Z)"
        title="Dossier zoeken (Ctrl+Z)"
        onClick={() => (open ? close() : setOpen(true))}
        style={{
          position: "relative",
          width: 44,
          height: 44,
          borderRadius: 9999,
          border: "1px solid #d1d5db",
          background: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={searchIcon} alt="" width={24} height={24} aria-hidden />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            left: 52,
            top: "50%",
            transform: "translateY(-50%)",
            width: 380,
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: 8,
          }}
        >
          <input
            ref={inputRef}
            // type="search" + de autofill-attributen voorkomen dat Safari/macOS
            // contactsuggesties ("Jansen …") over de resultaten heen legt.
            type="search"
            name="dossier-quicksearch"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            role="combobox"
            aria-autocomplete="list"
            aria-controls="dossier-quicksearch-resultaten"
            aria-expanded={resultaten.length > 0}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // Tab bladert door de resultaten (Shift+Tab terug); Enter opent
              // de geselecteerde rij.
              if (e.key === "Tab" && resultaten.length > 0) {
                e.preventDefault();
                setActiveIndex((i) =>
                  e.shiftKey
                    ? (i - 1 + resultaten.length) % resultaten.length
                    : (i + 1) % resultaten.length,
                );
              } else if (e.key === "Enter" && resultaten.length > 0) {
                const i = Math.min(activeIndex, resultaten.length - 1);
                openDossier(resultaten[i].kenmerk);
              }
            }}
            placeholder="Zoek op naam of kenmerk..."
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "6px 8px",
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              fontSize: 13,
            }}
          />
          {zoekterm === "" ? null : isLoading ? (
            <div style={{ padding: "8px 4px", color: "#6b7280", fontSize: 12 }}>
              Laden...
            </div>
          ) : resultaten.length === 0 ? (
            <div style={{ padding: "8px 4px", color: "#6b7280", fontSize: 12 }}>
              Geen resultaten
            </div>
          ) : (
            <div
              id="dossier-quicksearch-resultaten"
              style={{ maxHeight: 84, overflowY: "auto", marginTop: 6 }}
            >
              {/* maxHeight 84 = 3 rijen van 28px; meer resultaten scrollen */}
              <table
                className={tableStyles.table}
                style={{ width: "100%", tableLayout: "fixed" }}
              >
                <tbody>
                  {resultaten.map((d, i) => (
                    <tr
                      key={d.kenmerk}
                      ref={i === activeIndex ? activeRowRef : null}
                      className={tableStyles.clickableRow}
                      onClick={() => openDossier(d.kenmerk)}
                      style={
                        i === activeIndex
                          ? { background: "#eef2ff" }
                          : undefined
                      }
                    >
                      <td style={celStijl}>
                        {/* C5: elke rij op één regel; lange naam kapt af, het
                            (kleine) statuslabel blijft altijd zichtbaar. */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            minWidth: 0,
                          }}
                        >
                          <span
                            title={d.naam}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {d.naam}
                          </span>
                          {d.status !== "ACTIEF" && (
                            <DossierStatusBadge
                              status={d.status}
                              style={{
                                flexShrink: 0,
                                padding: "0 6px",
                                fontSize: 10,
                                lineHeight: "14px",
                              }}
                            />
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          ...celStijl,
                          color: "#6b7280",
                          whiteSpace: "nowrap",
                          width: 110,
                        }}
                      >
                        {d.kenmerk}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
